import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { EmpenhoRestoService } from 'contabil-lib';
import { Convenio, EmpenhoResto, FuncaoService, GlobalService, Login } from 'eddydata-lib';
import { Subject } from 'rxjs';
import { tsXLXS } from 'ts-xlsx-export';
import { ConfirmationService, MessageService } from 'primeng/api';
import { EmpenhoService } from '../../../contabil/empenho/service/empenho.service';

@Component({
  selector: 'lib-convenio-empenho',
  templateUrl: './convenio-empenho.component.html'
})

export class ConvenioEmpenhoComponent implements OnInit, OnChanges, OnDestroy {

  protected unsubscribe: Subject<void> = new Subject();
  public empenhos: any;
  public empenhosResto: any;
  public listaEmps: Array<any> = [];

  @Input() convenio: Convenio;
  @Input() login: Login;

  public paginaCorrente: number = 1;
  public paginaTotal: number;
  public limite: number = 20;
  public empenhosVisualizacao = []

  constructor(
    protected confirmationService: ConfirmationService,
    public globalService: GlobalService,
    protected funcaoService: FuncaoService,
    protected messageService: MessageService,
    protected empenhoService: EmpenhoService,
    protected empenhoRestoService: EmpenhoRestoService,
  ) { }

  async ngOnInit(): Promise<void> {
    await this.buscarEmpenhos();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  async ngOnChanges(changes: SimpleChanges) {
  }

  public async exportarXLSX() {
    const listaItens = new Array();
    for (const item of this.empenhos.content) {
      const entity = {
        tipo: 'EO',
        ano: item.exercicio?.ano,
        data_empenho: this.funcaoService.converteDataBR(item.data_empenho),
        numero: item?.numero,
        ficha_numero: item?.ficha?.numero,
        despesa: item?.subelemento?.codigo,
        recurso: `${item?.ficha?.recurso?.codigo}${item?.ficha?.aplicacao?.codigo}${item?.ficha?.aplicacao_variavel?.variavel}`,
        cpf_cnpj: item.favorecido?.cpf_cnpj,
        razao_social: item.favorecido?.nome,
        valor_empenhado: item.valor_empenho
      };
      listaItens.push(entity);
    }

    for (const item of this.empenhosResto.content) {
      const entity = {
        tipo: 'ER',
        ano: item.exercicio?.ano,
        data_empenho: this.funcaoService.converteDataBR(item.data_empenho),
        numero: item?.numero,
        ficha_numero: await this.buscarFichaDespesa(item),
        despesa: item?.subelemento,
        recurso: item?.recurso_variavel,
        cpf_cnpj: item.favorecido?.cpf_cnpj,
        razao_social: item.favorecido?.nome,
        valor_empenhado: item.valor_empenho
      };
      listaItens.push(entity);
    }
    tsXLXS().exportAsExcelFile(listaItens).saveAsExcelFile(`empenhos - ${this.convenio.numero} ${this.convenio.ano}`);
  }

  private cpfCnpj(doc: string) {
    if (doc?.length > 11)
      return this.funcaoService.mascarar('##.###.###/####-##', doc);
    else
      return this.funcaoService.mascarar('###.###.###-##', doc);
  }

  public async buscarFichaDespesa(empenhoResto: EmpenhoResto) {
    const empenhoOrc = await this.empenhoService.obter({
      'ignoreCondObrig': true,
      'exercicio.ano': empenhoResto.ano,
      'numero': empenhoResto.numero,
      'orgao.id': empenhoResto.orgao.id,
      'relations': 'ficha'
    }).toPromise()

    return empenhoOrc.ficha ? empenhoOrc.ficha.numero : 'XXXX';
  }

  public async proximaPagina() {
    if (this.paginaCorrente < this.paginaTotal) {
      this.paginaCorrente++;
      await this.buscarEmpenhos();
      window.scrollTo(0, 0);
    }
  }

  public async paginaAnterior() {
    this.paginaCorrente = this.paginaCorrente === 1 ? 1 : this.paginaCorrente - 1;
    await this.buscarEmpenhos();
    window.scrollTo(0, 0);
  }

  public async paginaDigitada(pagina: number) {
    this.paginaCorrente = pagina >= this.paginaTotal ? this.paginaTotal : pagina;
    await this.buscarEmpenhos();
    window.scrollTo(0, 0);
  }

  public async mudarLimite(limite: number) {
    this.limite = limite;
    this.paginaDigitada(1);
    await this.buscarEmpenhos();
  }

  public preencherEmpVisualizacao() {
    const inicio = (this.paginaCorrente - 1) * this.limite;
    const fim = inicio + this.limite;
    this.empenhosVisualizacao = this.listaEmps.slice(inicio, fim);
  }

  public async buscarEmpenhos(pagina: number = this.paginaCorrente) {
    // Busca empenhos resto
    this.empenhosResto = await this.empenhoRestoService
      .filtrar(pagina, this.limite, { convenio_id: this.convenio.id, ignoreCondObrig: true, relations: 'exercicio,favorecido,orgao' })
      .toPromise();

    // Busca empenhos
    this.empenhos = await this.empenhoService
      .filtrar(pagina, this.limite, { convenio_id: this.convenio.id, ignoreCondObrig: true, relations: 'exercicio,ficha.recurso,ficha.aplicacao,ficha.aplicacao_variavel,subelemento,favorecido' })
      .toPromise();

    for (const item of this.empenhosResto.content) {
      const entity = {
        impresso: item.impresso,
        tipo: 'ER',
        ano: item.exercicio?.ano,
        data_empenho: item.data_empenho,
        numero: item?.numero,
        ficha_numero: await this.buscarFichaDespesa(item),
        despesa: item?.subelemento,
        recurso: item?.recurso_variavel,
        cpf_cnpj: this.cpfCnpj(item?.favorecido?.cpf_cnpj),
        razao_social: item.favorecido?.nome,
        valor_empenhado: item.valor_empenho,
      };
      this.listaEmps.push(entity);
    }

    for (const item of this.empenhos.content) {
      const entity = {
        impresso: item.impresso,
        tipo: 'EO',
        ano: item.exercicio?.ano,
        data_empenho: item.data_empenho,
        numero: item?.numero,
        ficha_numero: item?.ficha?.numero,
        despesa: item?.subelemento?.codigo,
        recurso: `${item?.ficha?.recurso?.codigo}${item?.ficha?.aplicacao?.codigo}${item?.ficha?.aplicacao_variavel?.variavel}`,
        cpf_cnpj: this.cpfCnpj(item?.favorecido?.cpf_cnpj),
        razao_social: item.favorecido?.nome,
        valor_empenhado: item.valor_empenho,
      };
      this.listaEmps.push(entity);
    }
    this.paginaTotal = +this.empenhosResto.totalPages + +this.empenhos.totalPages;
    this.preencherEmpVisualizacao();
  }

}

